<template>
    <div class="ppv2-flex ppv2-items-start ppv2-gap-4 ppv2-mb-3">
        <!-- Date -->
        <div class="ppv2-w-1/3">
            <BaseDateInput
                :value="date"
                @input="$emit('update:date', $event)"
                :label="labels.date"
                customLabelClass="!ppv2-text-[#A09DBC]"
                :customSizeLabel="11"
            />
        </div>

        <!-- Periods -->
        <div class="ppv2-flex-1 ppv2-space-y-3">
            <div v-for="(period, idx) in periods" :key="'period-' + idx" class="ppv2-flex ppv2-items-end ppv2-gap-2">
                <div class="ppv2-flex-1">
                    <BaseTimeInput
                        v-model="period.start"
                        :label="labels.startTime"
                        :customSizeLabel="11"
                        customLabelClass="!ppv2-text-[#A09DBC]"
                        :defaultUse12h="is12H"
                    />
                </div>

                <div class="ppv2-mb-2.5 ppv2-text-black">→</div>

                <div class="ppv2-flex-1">
                    <BaseTimeInput
                        v-model="period.end"
                        :label="labels.endTime"
                        :customSizeLabel="11"
                        customLabelClass="!ppv2-text-[#A09DBC]"
                        :defaultUse12h="is12H"
                    />
                </div>

                <!-- Duration -->
                <div
                    class="bg-purple ppv2-bg-opacity-20 ppv2-mb-1 ppv2-h-[26px] ppv2-px-3 text-purple ppv2-rounded-[10px] ppv2-flex ppv2-items-center ppv2-justify-center ppv2-text-[10px] poppinsMedium"
                >
                    {{ calculateDuration(period.start, period.end) }}
                </div>

                <!-- Remove -->
                <button
                    v-if="allowMultiple && idx > 0"
                    type="button"
                    class="ppv2-w-[38px] ppv2-h-[38px] ppv2-rounded-xl ppv2-bg-gray-50 ppv2-border ppv2-border-gray-200 ppv2-flex ppv2-items-center ppv2-justify-center hover:ppv2-bg-red-50 hover:ppv2-text-red-500 ppv2-transition-colors"
                    @click="removePeriod(idx)"
                >
                    <Minus :size="16" />
                </button>
            </div>

            <!-- Add Schedule -->
            <button
                v-if="allowMultiple && periods.length < maxPeriods"
                type="button"
                class="ppv2-w-full ppv2-py-2.5 ppv2-border ppv2-border-dashed ppv2-border-black ppv2-border-opacity-[8%] ppv2-rounded-[7px] ppv2-text-xs ppv2-text-black ppv2-text-opacity-60 hover:border-purple hover:text-purple transition-colors flex items-center justify-center gap-1.5 !ppv2-mb-4"
                @click="addPeriod"
            >
                <Plus :size="12" />
                {{ labels.addSchedule }}
            </button>
        </div>
    </div>
</template>

<script>
import { Plus, Minus } from 'lucide-vue'
import BaseDateInput from '../../../Shared/BaseDateInput.vue'
import BaseTimeInput from '../../../Shared/BaseTimeInput.vue'

export default {
    name: 'PlanningPeriodSection',
    components: {
        Plus,
        Minus,
        BaseDateInput,
        BaseTimeInput,
    },
    props: {
        date: {
            type: String,
            required: true,
        },
        periods: {
            type: Array,
            required: true,
        },
        allowMultiple: {
            type: Boolean,
            default: false,
        },
        maxPeriods: {
            type: Number,
            default: 2,
        },
        labels: {
            type: Object,
            default: () => ({
                date: 'DATE',
                startTime: 'START TIME',
                endTime: 'END TIME',
                addSchedule: 'Add schedule',
            }),
        },
    },
    methods: {
        addPeriod() {
            const newPeriods = this.periods.map((p) => ({ ...p }))
            const lastEnd = newPeriods.length > 0 ? newPeriods[newPeriods.length - 1].end : '08:00'
            const newStart = this.addMinutes(lastEnd, 30)
            const endStart = this.addMinutes(newStart, 30)
            const newPeriod = {
                start: newStart,
                end: endStart,
            }

            this.$emit('update:periods', [...newPeriods, newPeriod])
        },
        removePeriod(idx) {
            const newPeriods = [...this.periods]
            newPeriods.splice(idx, 1)
            this.$emit('update:periods', newPeriods)
        },
        addMinutes(time, minutes) {
            const [h, m] = time.split(':').map(Number)
            const totalMinutes = h * 60 + m + minutes
            const newH = Math.floor(totalMinutes / 60) % 24
            const newM = totalMinutes % 60
            return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`
        },
        calculateDuration(start, end) {
            if (!start || !end) return '--'

            const [startH, startM] = start.split(':').map(Number)
            const [endH, endM] = end.split(':').map(Number)

            let diffH = endH - startH
            let diffM = endM - startM

            if (diffM < 0) {
                diffH -= 1
                diffM += 60
            }

            if (diffH < 0) {
                diffH += 24
            }

            if (diffH === 0 && diffM === 0) return '0h00'

            return `${diffH}h${String(diffM).padStart(2, '0')}`
        },
    },
    computed: {
        is12H() {
            return localStorage.getItem('locale') === 'en'
        },
    },
}
</script>
